import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, getUTM, track, formatApiError, waLink } from "@/lib/api";

const BUSINESS_TYPES = ["Small Business", "Growing Company", "NGO", "Training Organization", "Skill Development Organization", "Educational Institute", "Multi-Center Organization", "Other"];

export default function LeadForm({ leadType = "demo", defaultPlan, title, subtitle }) {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", organization: "", business_type: "", interested_plan: defaultPlan || "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", {
        ...form,
        business_type: form.business_type || null,
        interested_plan: form.interested_plan || null,
        lead_type: leadType,
        landing_page: window.location.pathname,
        utm: getUTM(),
      });
      track(leadType === "demo" ? "demo_request" : "lead_submit", { lead_type: leadType });
      setDone(true);
      toast.success("Request received. Our team will contact you shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const waMessage = [
      `New ${leadType === "demo" ? "Demo Request" : "Enquiry"} — Mashara Finance website`,
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Mobile: ${form.mobile}`,
      form.organization ? `Organization: ${form.organization}` : null,
      form.business_type ? `Business Type: ${form.business_type}` : null,
      form.interested_plan ? `Interested Plan: ${form.interested_plan}` : null,
      form.message ? `Message: ${form.message}` : null,
    ].filter(Boolean).join("\n");
    return (
      <div data-testid="lead-form-success" className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-emerald-700 text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">✓</div>
        <h3 className="font-display font-bold text-xl text-slate-900 mb-2">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="text-slate-600">Your {leadType === "demo" ? "demo request" : "message"} has been received. Our team will reach out within one business day.</p>
        <a href={waLink(waMessage)} target="_blank" rel="noopener noreferrer"
          data-testid="lead-success-whatsapp-button"
          onClick={() => track("whatsapp_click", { location: "lead_success", lead_type: leadType })}
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#25D366] text-white px-6 py-3 font-semibold text-sm hover:opacity-90 transition-opacity">
          Send your details instantly on WhatsApp
        </a>
        <p className="text-xs text-slate-500 mt-3">Tap above to deliver your request straight to our team's WhatsApp (+91 91620 55572) for the fastest response.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-form" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-4">
      {(title || subtitle) && (
        <div className="mb-2">
          {title && <h3 className="font-display font-bold text-xl text-slate-900">{title}</h3>}
          {subtitle && <p className="text-sm text-slate-600 mt-1">{subtitle}</p>}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="lead-name">Full Name *</Label>
          <Input id="lead-name" data-testid="lead-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lead-mobile">Mobile *</Label>
          <Input id="lead-mobile" data-testid="lead-mobile-input" required value={form.mobile} onChange={set("mobile")} placeholder="98765 43210" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="lead-email">Work Email *</Label>
          <Input id="lead-email" data-testid="lead-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lead-org">Organization</Label>
          <Input id="lead-org" data-testid="lead-org-input" value={form.organization} onChange={set("organization")} placeholder="Organization name" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Business Type</Label>
          <Select value={form.business_type} onValueChange={set("business_type")}>
            <SelectTrigger data-testid="lead-business-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              {BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Interested Plan</Label>
          <Select value={form.interested_plan} onValueChange={set("interested_plan")}>
            <SelectTrigger data-testid="lead-plan-select"><SelectValue placeholder="Not sure yet" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="business">Business</SelectItem>
              <SelectItem value="enterprise">Enterprise / Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="lead-message">Message</Label>
        <Textarea id="lead-message" data-testid="lead-message-input" rows={3} value={form.message} onChange={set("message")}
          placeholder="Tell us about your requirements (optional)" />
      </div>
      <Button type="submit" data-testid="lead-submit-button" disabled={loading}
        className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white py-6 text-base font-semibold">
        {loading ? "Submitting…" : leadType === "demo" ? "Book My Demo" : "Send Message"}
      </Button>
      <p className="text-xs text-slate-500 text-center">We respond within one business day. Your details are never shared.</p>
    </form>
  );
}
